// eslint-disable-next-line import/no-named-as-default
import Icon from "@mdi/react";
import { useEffect, useMemo, useRef } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { useDispatch } from "react-redux";

import { Equalizer } from "@material-ui/icons";
import { mdiChartAreaspline, mdiChartBellCurveCumulative, mdiChartLine } from "@mdi/js";
import { Tooltip } from "antd";
import { EvaChart } from "constants/charts.enums";
import { ChartStatus, useChartsContext } from "contexts/ChartContext";
import { UNDEFINED_LOCKS } from "entities/charts/chart";
import {
  DEFAULT_LOG_AXIS_FEATURE_ENTITY,
  createFeatureEntities,
  restoreNormalizationUnitsSentinel
} from "entities/charts/eva/features";
import { updateChart, updateChartByPath } from "store/features";
import styled from "styled-components";
import { getChartType, noop, toggleForecastLabels } from "utils";

import { useChartEntities } from "hooks/charts";
import useFeatureFlags from "hooks/useFeatureFlags";

import { EntityKind } from "models/entityKind";

import { BaseMenu, BaseTooltip } from "components/base";
import { useChartTypeHandler } from "components/charts/hooks/useChartTypeHandler";
import { RecoveryPVI, RecoveryTime } from "components/icons";
import BoxPlot from "components/icons/BoxPlot";
import CrossPlot from "components/icons/CrossPlot";
import CumTime from "components/icons/CumTime";
import DeclineRate from "components/icons/DeclineRate";
import MaterialBalanceSheet from "components/icons/MaterialBalanceSheet";
import Mosaic from "components/icons/Mosaic";
import PieChart from "components/icons/PieChart";
import Probit from "components/icons/Probit";
import RateCum from "components/icons/RateCum";
import RateDate from "components/icons/RateDate";
import RateTime from "components/icons/RateTime";
import StackedBar from "components/icons/StackedBar";
import StackedBarHorizontal from "components/icons/StackedBarHorizontal";
import TotalRateCum from "components/icons/TotalRateCum";
import TotalRateDate from "components/icons/TotalRateDate";
import TrendDate from "components/icons/TrendDate";

import {
  ChartType,
  ChartTypeLabels,
  DEFAULT_CHART_PRODUCT,
  DEFAULT_MIDSTREAM_CHART_PRODUCT,
  MULTI_PRODUCT_COMPATIBLE_CHART_TYPES,
  filterChartTypeGroups,
  getMidstreamChartTypes
} from "../../../../constants";
import ChartTypeHeader from "../ChartTypeHeader";
import "./ChartSelector.scss";
import { ChartTypeButton, ChartTypeMenuContainer } from "./ChartSelector.styles";
import { applyMaterialBalanceTimeSettingsOnChartSwitch } from "./utils/applyMaterialBalanceTimeSettingsOnChartSwitch";
import { handleFeaturesOnChartTypeSwitch } from "./utils/handleFeaturesOnChartTypeSwitch";

// Keyed by chart type label (ChartTypeLabels) rather than position, so the icon for a chart
// type can't drift out of sync with CHART_TYPE_LABELS/ChartType when a new chart type is
// inserted anywhere but the end of ALL_CHART_TYPES — Record<ChartTypeLabels, ...> forces every
// label to have an icon at compile time.
export const CHART_TYPE_ICONS: Record<ChartTypeLabels, JSX.Element> = {
  "Rate Cum": <RateCum key="rate-cum" />,
  "Rate Time": <RateTime key="rate-time" />,
  "Rate Date": <RateDate key="rate-date" />,
  "Total Rate Date": <TotalRateDate key="total-rate-date" />,
  "Cum Time": <CumTime key="cum-time" />,
  "Cross Plot": <CrossPlot key="cross-plot" />,
  Probit: <Probit key="probit" />,
  "Box Plot": <BoxPlot key="box-plot" />,
  "Stacked Bar": <StackedBar key="stacked-bar" />,
  "Horizontal Stacked Bar": <StackedBarHorizontal key="stacked-bar-horizontal" />,
  Pie: <PieChart key="pie-chart" />,
  Mosaic: <Mosaic key="mosaic" />,
  "Material Balance Time": <MaterialBalanceSheet key="material-balance-sheet" />,
  "Total Rate Cum": <TotalRateCum key="total-rate-cum" />,
  CAGR: <Icon path={mdiChartLine} size={1.8} key="cagr" />,
  "Decline Rate": <DeclineRate key="base-decline-rate" />,
  "Trend Date": <TrendDate key="trend-date" />,
  "Well Contribution": (
    <Icon path={mdiChartAreaspline} size={1.8} key="well-contribution-cum-date" />
  ),
  "RF vs PV": <RecoveryPVI key="recovery-pvi" />,
  "RF vs Time": <RecoveryTime key="recovery-time" />,
  "Injector Count Date": (
    <Icon path={mdiChartBellCurveCumulative} size={1.8} key="injector-total" />
  ),
  // Rig/Frac Charts widget only (EVA-76) — never rendered here since these types are
  // selected via RigFracChartSelector, not this menu, but Record<ChartTypeLabels, ...>
  // requires every label to have an entry.
  "Activity vs Time": <Equalizer fontSize="large" key="rig-frac-activity" />,
  "Recent Activity": <Equalizer fontSize="large" key="rig-frac-recent-activity" />,
  // Well Status widget only — same story: selected via
  // WellStatusChartSelector, present here only to satisfy the Record.
  "Well Status vs Time": <Equalizer fontSize="large" key="well-status-over-time" />,
  "Well Status Current": <Equalizer fontSize="large" key="well-status-current" />
};

// Multi product icons live in components/multiphase-chart/constants/chartTypeIcons.

interface ChartSelectorProps {
  onMenuOpened?: () => void;
  registerCloseMenu?: (fn: () => void) => void;
}

export function ChartSelector({
  onMenuOpened,
  registerCloseMenu
}: ChartSelectorProps = {}) {
  useChartTypeHandler();
  const { id, entityKind, setStatus } = useChartsContext();
  const { chartType, forecast, chartTypeParam, multiProduct } = useChartEntities(id);
  const isMultiProductActive = multiProduct?.active;
  const { hasFeature: hasFeatureFlag } = useFeatureFlags();
  // the midstream charts that exist on the well charts are the same to keep functionality
  // the same in existing logic (such as below) but for features, highlighting and requests
  // it's identified as a different chart
  const originalChartType = chartType?.replace(" Midstream", "");
  const dispatch = useDispatch();

  if (!chartType) {
    if (entityKind === EntityKind.Well) {
      dispatch(
        updateChart({
          id,
          chartType: EvaChart.RateCum,
          features: createFeatureEntities(EvaChart.RateCum),
          product: DEFAULT_CHART_PRODUCT,
          response: undefined
        })
      );
    } else {
      dispatch(
        updateChart({
          id,
          chartType: EvaChart.CrossPlotMidstream,
          features: createFeatureEntities(EvaChart.CrossPlotMidstream),
          product: DEFAULT_MIDSTREAM_CHART_PRODUCT,
          response: undefined
        })
      );
    }
  }

  useEffect(() => {
    const { x, y } = chartTypeParam.properties;
    const xField = toggleForecastLabels(x, forecast?.active);
    const yField = toggleForecastLabels(y, forecast?.active);
    dispatch(
      updateChartByPath({
        id,
        path: "features.chartTypeParam.properties",
        value: {
          x: xField,
          y: yField
        }
      })
    );
  }, [forecast.active]);

  useHotkeys("tab", noop, []);

  const closeChartMenuRef = useRef<(() => void) | null>(null);

  // Memoized: entityKind/hasFeatureFlag rarely change, but this was
  // otherwise recomputed (new array identity) on every render, which in turn
  // defeated memoizing gatedChartTypes below (see useChartTypeHandler.tsx's
  // getMidstreamChartTypes memo for the sibling fix).
  const availableChartTypes = useMemo(
    () =>
      entityKind == EntityKind.Well
        ? []
        : getMidstreamChartTypes(hasFeatureFlag("Midstream Allocation Model")),
    [entityKind, hasFeatureFlag]
  );

  // EVA-10: hide chart types whose org-level feature flag is disabled.
  const gatedChartTypes = useMemo(
    () =>
      filterChartTypeGroups(
        availableChartTypes?.length > 0 ? availableChartTypes : ChartType,
        hasFeatureFlag
      ),
    [availableChartTypes, hasFeatureFlag]
  );

  return (
    <div className="charts-selector flex">
      {/* use Base components because antd would automatically open and popover location was not desirable, discovered in EVA-4042 */}
      <BaseTooltip text={chartType}>
        <BaseMenu
          className="chart-type-base-menu"
          onOpen={onMenuOpened}
          trigger={
            <IconDropdown data-testid={"chartTypeSelector"}>
              {CHART_TYPE_ICONS[originalChartType as ChartTypeLabels]}
            </IconDropdown>
          }>
          {({ closeMenu }) => {
            closeChartMenuRef.current = closeMenu;
            registerCloseMenu?.(closeMenu);
            return (
              <ChartTypeMenuContainer>
                {gatedChartTypes.map((header) => (
                  <ChartTypeHeader key={header.name} header={header.name}>
                    {header.items.map((ct) => {
                      return (
                        <Tooltip
                          key={ct}
                          title={
                            isMultiProductActive &&
                            !MULTI_PRODUCT_COMPATIBLE_CHART_TYPES.includes(ct as EvaChart)
                              ? "Please disable multi product to use this chart."
                              : getChartType(ct)?.tooltip ?? ""
                          }>
                          <ChartTypeButton
                            onClick={() => {
                              if (
                                isMultiProductActive &&
                                !MULTI_PRODUCT_COMPATIBLE_CHART_TYPES.includes(
                                  ct as EvaChart
                                )
                              ) {
                                return;
                              }
                              closeMenu();
                              const sessionDataStr = sessionStorage.getItem(
                                `${
                                  entityKind === EntityKind.Well
                                    ? "chart"
                                    : "midstream-chart"
                                }::${id}`
                              );
                              const sessionDataObj = sessionDataStr
                                ? JSON.parse(sessionDataStr)
                                : {};
                              handleFeaturesOnChartTypeSwitch(
                                ct as EvaChart,
                                chartType,
                                sessionDataObj
                              );

                              const overrides = createFeatureEntities(
                                `${ct}${
                                  entityKind === EntityKind.Facility ? " Midstream" : ""
                                }` as EvaChart,
                                {
                                  ...sessionDataObj?.features,
                                  logAxis: DEFAULT_LOG_AXIS_FEATURE_ENTITY //reset log axis to default when switching chart types
                                }
                              );
                              restoreNormalizationUnitsSentinel(
                                overrides,
                                sessionDataObj?.features?.normalization?.properties?.units
                              );
                              if (ct === EvaChart.MaterialBalanceTime) {
                                applyMaterialBalanceTimeSettingsOnChartSwitch(
                                  sessionDataObj,
                                  overrides
                                );
                              }

                              setStatus(ChartStatus.Loading);

                              dispatch(
                                updateChart({
                                  id,
                                  chartType: `${ct}${
                                    entityKind === EntityKind.Facility ? " Midstream" : ""
                                  }`,
                                  product:
                                    sessionDataObj?.product ??
                                    (entityKind === EntityKind.Well
                                      ? DEFAULT_CHART_PRODUCT
                                      : DEFAULT_MIDSTREAM_CHART_PRODUCT),
                                  features: overrides,
                                  locks: UNDEFINED_LOCKS,
                                  response: undefined
                                })
                              );
                            }}
                            key={ct}
                            ellipses={false}
                            appearance=""
                            className={`${
                              ct === originalChartType
                                ? "isSelected"
                                : isMultiProductActive &&
                                  !MULTI_PRODUCT_COMPATIBLE_CHART_TYPES.includes(
                                    ct as EvaChart
                                  )
                                ? "readonlyButton"
                                : "isAvailable"
                            }`}>
                            {CHART_TYPE_ICONS[ct]}
                            <span>{ct}</span>
                          </ChartTypeButton>
                        </Tooltip>
                      );
                    })}
                  </ChartTypeHeader>
                ))}
              </ChartTypeMenuContainer>
            );
          }}
        </BaseMenu>
      </BaseTooltip>
    </div>
  );
}

const IconDropdown = styled.div`
  color: #a2aaad;
  transition: color var(--duration-short);
  cursor: pointer;
  display: flex;
  &:hover {
    color: var(--color-primary);
  }
  margin-left: 21px;
`;
